import React from "react"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { EraUploadFileUploadStatus } from "../../../config/integrations.config"
import type { IEraUploadFileReviewState } from "../../../types/integrations.types"
import { EraUploadReviewX12PanelComponent } from "./eraUploadReviewX12Panel.component"
import { EraUploadTableComponent } from "./eraUploadTable.component"

export const EraUploadReviewFileSectionComponent: React.FunctionComponent<
    IEraUploadReviewFileSectionComponentProps
> = props => {
    const file = props.file

    const isX12File = props.fileFormat === OneVerifyEraUploadFileFormat.x12

    const isPreviewTruncated = file.totalRowCount > file.rows.length

    const isUploading = file.uploadStatus === EraUploadFileUploadStatus.uploading

    const isSucceeded =
        file.uploadStatus === EraUploadFileUploadStatus.succeeded

    const isFailed = file.uploadStatus === EraUploadFileUploadStatus.failed

    const actionsDisabled = props.isBatchUploading || isUploading || isSucceeded

    const statusBadge = (() => {
        if (isSucceeded) {
            return (
                <span className="text-brand-green3 font-semibold text-[13px]">
                    Uploaded
                </span>
            )
        }

        if (isFailed) {
            return (
                <span className="text-brand-error font-semibold text-[13px]">
                    Upload failed
                </span>
            )
        }

        if (isUploading) {
            return (
                <span className="text-brand-grey7 font-semibold text-[13px]">
                    Uploading...
                </span>
            )
        }

        if (!file.isValid) {
            return (
                <span className="text-brand-error font-semibold text-[13px]">
                    Needs attention
                </span>
            )
        }

        return (
            <span className="text-brand-green3 font-semibold text-[13px]">
                Ready
            </span>
        )
    })()

    const actionButtons = (
        <div className="flex flex-wrap items-center gap-[8px]">
            {isFailed ? (
                <ButtonElement
                    label="Retry"
                    htmlType="button"
                    type="primary"
                    onClick={() => {
                        props.onRetryUpload(file.id)
                    }}
                    disabled={props.isBatchUploading}
                    buttonClassName="!pl-[12px] !pr-[12px]"
                />
            ) : null}

            <ButtonElement
                label="Replace"
                htmlType="button"
                type="default"
                onClick={() => {
                    props.onReplaceFile(file.id)
                }}
                disabled={actionsDisabled}
                buttonClassName="!pl-[12px] !pr-[12px]"
            />

            <ButtonElement
                label="Remove"
                htmlType="button"
                type="default"
                onClick={() => {
                    props.onRemoveFile(file.id)
                }}
                disabled={actionsDisabled}
                buttonClassName="!pl-[12px] !pr-[12px]"
            />
        </div>
    )

    return (
        <section
            className={`
                w-full rounded-[8px] border border-solid border-brand-warmLight2
                bg-brand-white px-[24px] py-[24px]
                ${props.className ?? ""}
            `}
        >
            <div className="flex flex-col gap-[16px]">
                <div className="flex flex-wrap items-center justify-between gap-[12px]">
                    {statusBadge}
                    {actionButtons}
                </div>

                {!file.isValid ? (
                        <PrimaryText color={NIRVANA_COLORS.brand.error}>
                            {file.validationError ??
                                "This file could not be validated."}
                        </PrimaryText>
                    ) : isX12File ? (
                        <EraUploadReviewX12PanelComponent
                            file={file}
                            embedded
                        />
                    ) : (
                        <div className="w-full">
                            {isPreviewTruncated ? (
                                <PrimaryText
                                    className="mb-[12px]"
                                    typography="subtitle"
                                    color={NIRVANA_COLORS.brand.grey7}
                                >
                                    Previewing first {file.rows.length} rows —{" "}
                                    {file.totalRowCount.toLocaleString()} total
                                    in this file.
                                </PrimaryText>
                            ) : null}

                            <div
                                className="
                                    relative w-[calc(100%+48px)] -mx-[24px]
                                    overflow-x-auto
                                "
                            >
                                <EraUploadTableComponent rows={file.rows} />
                            </div>
                        </div>
                    )}

                    {isFailed && file.uploadError ? (
                        <PrimaryText color={NIRVANA_COLORS.brand.error}>
                            {file.uploadError}
                        </PrimaryText>
                    ) : null}
            </div>
        </section>
    )
}

interface IEraUploadReviewFileSectionComponentProps {
    className?: string
    fileFormat: OneVerifyEraUploadFileFormat
    file: IEraUploadFileReviewState
    isBatchUploading: boolean
    onRemoveFile: (fileId: string) => void
    onReplaceFile: (fileId: string) => void
    onRetryUpload: (fileId: string) => void
}
